import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NumberingSeries } from '~/types/user';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { generateEntryNumber } from '~/utils/string';

import { NumberingSeriesMobileCell } from './NumberingSeriesTableCells/NumberingSeriesMobileCell';
import { NumberingSeriesNameCell } from './NumberingSeriesTableCells/NumberingSeriesNameCell';
import { NumberingSeriesNextNumberCell } from './NumberingSeriesTableCells/NumberingSeriesNextNumberCell';
import { NumberingSeriesOptionsCell } from './NumberingSeriesTableCells/NumberingSeriesOptionsCell';
import { NumberingSeriesTypeCell } from './NumberingSeriesTableCells/NumberingSeriesTypeCell';
import { getNumberingSeriesType } from './utils';

export type DialogHandles = Record<'deleteNumberingSeriesHandle', AlertDialog.Handle<NumberingSeries>>;
export type TableActions = {
  onEditFormatAndOptions: (numberingSeries: NumberingSeries) => void;
  onEditNameAndType: (numberingSeries: NumberingSeries) => void;
};

const columnHelper = createAppColumnHelper<NumberingSeries>();

export const useNumberingSeriesTableColumns = ({ actions, dialogHandles }: { actions: TableActions; dialogHandles: DialogHandles }) => {
  const { t } = useTranslation(['settings']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Name
        columnHelper.accessor('name', {
          id: ColumnIds.numberingSeriesName,
          header: t('settings:general.numberingSeries.headers.name'),
          cell: ({ getValue }) => <NumberingSeriesNameCell name={getValue()} />,
          meta: {
            styles: { size: '2fr', minSize: 100 },
            isResizable: true,
          },
        }),

        // Type
        columnHelper.accessor((numberingSeries) => getNumberingSeriesType(numberingSeries), {
          id: ColumnIds.numberingSeriesType,
          header: t('settings:general.numberingSeries.headers.type'),
          cell: ({ getValue }) => <NumberingSeriesTypeCell type={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 75 },
            isResizable: true,
          },
        }),

        // Next number
        columnHelper.accessor((numberingSeries) => generateEntryNumber(numberingSeries), {
          id: ColumnIds.numberingSeriesNextNumber,
          header: t('settings:general.numberingSeries.headers.nextNumber'),
          cell: ({ getValue }) => <NumberingSeriesNextNumberCell nextNumber={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 100 },
            isResizable: true,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: numberingSeries } }) => (
            <NumberingSeriesOptionsCell actions={actions} dialogHandles={dialogHandles} numberingSeries={numberingSeries} />
          ),
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.numberingSeriesMobile,
          cell: ({ row: { original: numberingSeries } }) => (
            <NumberingSeriesMobileCell actions={actions} dialogHandles={dialogHandles} numberingSeries={numberingSeries} />
          ),
          meta: { mobile: true },
        }),
      ]),
    [actions, t, dialogHandles],
  );
};
